import React, { useRef, useState } from "react";

import { useAnalytics } from "@accurx/analytics";
import {
    NhsOrganisationDto,
    NhsOrganisationsSearchResponse,
} from "@accurx/api/account";
import { useCurrentWorkspace } from "@accurx/auth";
import { Ds } from "@accurx/design";
import { AutoFocusHeading } from "@accurx/navigation";
import { useSearchMappingOrganisationsQuery } from "@accurx/workspace-management";
import { useHistory, useLocation } from "react-router-dom";

import {
    StyledCardItem,
    StyledMain,
    StyledPageWrapper,
} from "app/workspace/pages/shared/Layout/Layout.styles";

import {
    PAGE_PARAM_NAME,
    SEARCH_TERM_PARAM_NAME,
} from "../../../shared/OrganisationSearch/constants";
import {
    SearchSchema,
    getQueryString,
} from "../../../shared/OrganisationSearch/utils";
import { MapWorkspaceCollapsibleInfo } from "../MapWorkspaceCollapsibleInfo";
import { OrganisationList } from "./OrganisationList";

export const MapWorkspaceSelectOrg = ({
    suggestions,
    suggestionsFetchStatus,
    addOrgClick,
    onSelectOrganisation,
}: {
    suggestions?: NhsOrganisationsSearchResponse;
    suggestionsFetchStatus: string;
    addOrgClick: () => void;
    onSelectOrganisation: (org: NhsOrganisationDto) => void;
}) => {
    const history = useHistory();
    const location = useLocation();
    const track = useAnalytics();

    const { organisationName: workspaceName, orgId: workspaceId } =
        useCurrentWorkspace();

    const onSelect = (
        org: NhsOrganisationDto,
        isRecommended: boolean = false,
    ) => {
        track("WorkspaceNationalCodeMappingOrganisationSelect Button Click", {
            isRecommended,
        });
        onSelectOrganisation(org);
    };

    const params = new URLSearchParams(location.search);
    const searchTermParam = params.get(SEARCH_TERM_PARAM_NAME) || "";
    const pageParam = Number(params.get(PAGE_PARAM_NAME)) || 1;

    const pageWrapperRef = useRef<HTMLElement>(null);

    const [searchTerm, setSearchTerm] = useState(searchTermParam);
    const [inputError, setInputError] = useState("");

    const {
        data: searchResults,
        refetch: searchRefetch,
        fetchStatus: searchFetchStatus,
    } = useSearchMappingOrganisationsQuery({
        workspaceId,
        searchTerm: searchTermParam,
        page: pageParam,
    });

    const validateSearch = (): boolean => {
        const result = SearchSchema.safeParse({
            searchTerm: searchTerm.trim(),
        });

        if (result.success) {
            setInputError("");
            return true;
        }

        const { message } = result.error.issues[0];
        setInputError(message);
        return false;
    };

    const onSubmitSearch = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateSearch()) {
            // Wipe the search query if the user's input doesn't meet our requirements
            history.push({
                search: getQueryString({
                    currentQuery: location.search,
                }),
            });

            return;
        }

        // Allow users to re-search for a more natural UX
        if (searchTermParam === searchTerm.trim()) {
            await searchRefetch();
        }

        history.push({
            search: getQueryString({
                currentQuery: location.search,
                searchTerm: searchTerm.trim(),
            }),
        });
    };

    const showPreviousResults = () => {
        history.push({
            search: getQueryString({
                currentQuery: location.search,
                searchTerm: searchTermParam,
                page: pageParam - 1,
            }),
        });
        pageWrapperRef.current?.scrollTo(0, 0);
    };

    const showNextResults = () => {
        history.push({
            search: getQueryString({
                currentQuery: location.search,
                searchTerm: searchTermParam,
                page: pageParam + 1,
            }),
        });
        pageWrapperRef.current?.scrollTo(0, 0);
    };

    return (
        <StyledPageWrapper ref={pageWrapperRef} flexDirection="column">
            <StyledMain gap="3" flexDirection="column">
                <AutoFocusHeading size="xlarge" as="h1">
                    Select an organisation to continue
                </AutoFocusHeading>
                <Ds.Text>
                    For an exact result you can search by ODS (Organisation Data
                    Service) code. Otherwise, search by name or postcode.
                </Ds.Text>

                <MapWorkspaceCollapsibleInfo
                    workspaceName={workspaceName}
                    addOrgClick={addOrgClick}
                />

                <Ds.Form onSubmit={onSubmitSearch}>
                    <Ds.Flex gap="2">
                        <Ds.Flex.Item flex="1 1 auto">
                            <Ds.Form.Field name="search-input">
                                <Ds.Form.Control>
                                    <Ds.TextInput
                                        inputMode="search"
                                        type="search"
                                        placeholder="Search for an organisation"
                                        aria-label="Search for an organisation"
                                        isInvalid={Boolean(inputError)}
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                    />
                                </Ds.Form.Control>
                                {inputError && (
                                    <Ds.Form.Error>{inputError}</Ds.Form.Error>
                                )}
                            </Ds.Form.Field>
                        </Ds.Flex.Item>
                        <Ds.Form.Submit>
                            <Ds.Button.Icon name="Search" />
                            Search
                        </Ds.Form.Submit>
                    </Ds.Flex>
                </Ds.Form>

                {suggestionsFetchStatus === "fetching" ||
                searchFetchStatus === "fetching" ? (
                    <Ds.Flex justifyContent="center" pt="1rem">
                        <Ds.Spinner size="medium" />
                    </Ds.Flex>
                ) : (
                    <>
                        {/* If the user searched but there's no results, prompt them to Submit a new org */}
                        {!!searchResults?.nhsOrganisations &&
                            searchResults.nhsOrganisations.length === 0 && (
                                <StyledCardItem>
                                    <p>
                                        Can't find your organisation? Submit the
                                        organisation's details here for review.
                                    </p>
                                    <Ds.Button
                                        appearance="secondary"
                                        onClick={() => addOrgClick()}
                                    >
                                        Submit an organisation
                                    </Ds.Button>
                                </StyledCardItem>
                            )}

                        {/* Display search results if available - if not, show Suggestions */}
                        {!!searchResults?.nhsOrganisations?.length ? (
                            <OrganisationList
                                title="Search results"
                                data={searchResults}
                                type="search"
                                onSelectOrganisation={(org) => onSelect(org)}
                                showPreviousResults={showPreviousResults}
                                showNextResults={showNextResults}
                            />
                        ) : (
                            !!suggestions?.nhsOrganisations?.length && (
                                <OrganisationList
                                    title="Suggested organisations (based on postcode)"
                                    data={suggestions}
                                    type="suggestions"
                                    onSelectOrganisation={(org) =>
                                        onSelect(org, true)
                                    }
                                    showPreviousResults={showPreviousResults}
                                    showNextResults={showNextResults}
                                />
                            )
                        )}
                    </>
                )}
            </StyledMain>
        </StyledPageWrapper>
    );
};
